import { useRuntimeBrandStore } from '@core/store/runtimeBrandStore';
import { useRuntimeThemeStore } from '@core/store/runtimeThemeStore';
import { setRuntimeApiKey } from '@core/store/runtimeApiStore';
import {
  getContactDetails,
  getIndustryTheme,
  getPolicies,
  getSocialLinks,
} from '@core/settings/ecommerceSettings';
import {
  applyThemeCssVars,
  parseThemeSettings,
  toRuntimeThemeState,
} from '@core/settings/themeSettings';

function getAccountDetails(account) {
  return account?.accountDetails || account?.account_details || account || {};
}

function getAccountName(account) {
  const details = getAccountDetails(account);
  return (
    String(
      details.business_name ||
        details.brand_name ||
        details.name ||
        details.email ||
        account?.name ||
        account?.email ||
        account?.id ||
        '',
    ).trim() || ''
  );
}

function getAccountBusinessAddress(account) {
  const details = getAccountDetails(account);
  const address = details.address || {};
  if (typeof address === 'string' && address) return address;
  const parts = [
    address.address_line_1 || '',
    address.address_line_2 || '',
    address.city || '',
    address.state || '',
    address.zip_code || '',
  ].filter(Boolean);
  return parts.join(', ');
}

function getAccountBusinessPhone(account) {
  const details = getAccountDetails(account);
  const mobile = details.mobile_number || {};
  return (
    (mobile.calling_code && mobile.number ? `+${mobile.calling_code} ${mobile.number}` : '') ||
    mobile.number ||
    mobile.full_number ||
    ''
  );
}

function getAccountBusinessEmail(account) {
  const details = getAccountDetails(account);
  return getContactDetails(details.settings?.ecommerce_settings, {
    fallbackEmail: details.email || account?.email,
  }).email;
}

function getAccountSupportPhone(account) {
  return getContactDetails(getAccountDetails(account).settings?.ecommerce_settings).supportPhone;
}

function getAccountWhatsappNumber(account) {
  return getContactDetails(getAccountDetails(account).settings?.ecommerce_settings).whatsappNumber;
}

function getAccountSocialLinks(account) {
  return getSocialLinks(getAccountDetails(account).settings?.ecommerce_settings);
}

function getAccountIndustryTheme(account) {
  return getIndustryTheme(getAccountDetails(account).settings?.ecommerce_settings);
}

function getAccountTheme(account) {
  const details = getAccountDetails(account);
  const parsed = parseThemeSettings(details.settings?.theme_settings, {
    logoFallbacks: [
      details.theme_settings?.logo_url,
      details.theme_settings?.logo?.url,
      details.logo?.url,
      details.logo_url,
      details.logoUrl,
    ],
  });
  return toRuntimeThemeState(parsed, details.settings?.ecommerce_settings);
}

function getBrandId(input = {}) {
  const brand = input?.brand || input?.brand_details || {};
  return String(
    input?.brandId ||
      input?.brand_id ||
      input?.org_brand_id ||
      brand.org_brand_id ||
      brand.brand_id ||
      brand.id ||
      '',
  ).trim();
}

function getKeyValue(key = {}) {
  return key?.FullApiKey || key?.fullApiKey || key?.full_api_key || key?.apiKey || null;
}

function pickApiKey(source = {}, { brandId } = {}) {
  const apiKeys = Array.isArray(source?.api_keys) ? source.api_keys : [];
  const normalizedBrandId = String(brandId || getBrandId(source) || '').trim();

  if (normalizedBrandId && apiKeys.length > 0) {
    const matchingBrandKey = apiKeys.find((key) => {
      const ids = Array.isArray(key?.org_brand_ids) ? key.org_brand_ids : [];
      return ids.some((id) => String(id).trim() === normalizedBrandId) && getKeyValue(key);
    });
    if (matchingBrandKey) return getKeyValue(matchingBrandKey);
  }

  const firstUsableKey = apiKeys.find((key) => key?.is_active !== false && !key?.is_deleted);
  return (
    source?.apiKey ||
    source?.fullApiKey ||
    source?.FullApiKey ||
    source?.full_api_key ||
    getKeyValue(firstUsableKey) ||
    getKeyValue(apiKeys[0]) ||
    null
  );
}

export function applyAccountRuntime(account) {
  const theme = getAccountTheme(account);
  const brandName = getAccountName(account);

  applyThemeCssVars(theme);
  useRuntimeThemeStore.getState().setThemeColors(theme);
  useRuntimeBrandStore.getState().setLogoUrl(theme.logoUrl || '');
  useRuntimeBrandStore.getState().setBrandName(brandName);
  useRuntimeBrandStore.getState().setBusinessAddress(getAccountBusinessAddress(account));
  useRuntimeBrandStore.getState().setBusinessPhone(getAccountBusinessPhone(account));
  useRuntimeBrandStore.getState().setBusinessEmail(getAccountBusinessEmail(account));
  useRuntimeBrandStore.getState().setSupportPhone(getAccountSupportPhone(account));
  useRuntimeBrandStore.getState().setWhatsappNumber(getAccountWhatsappNumber(account));
  useRuntimeBrandStore.getState().setSocialLinks(getAccountSocialLinks(account));

  return {
    brandName,
    theme,
  };
}

function getOrgSettings(input = {}) {
  return input?.account_settings || input?.settings || input?.accountDetails?.settings || {};
}

function getOrgBrandName(input = {}) {
  const brand = input?.brand || input?.brand_details || {};
  return (
    String(
      brand.display_name ||
        brand.name ||
        input?.company_name ||
        input?.business_name ||
        input?.name ||
        '',
    ).trim() || ''
  );
}

function getOrgTheme(input = {}) {
  const settings = getOrgSettings(input);
  const brand = input?.brand || input?.brand_details || {};
  const parsed = parseThemeSettings(settings.theme_settings, {
    logoFallbacks: [brand.logo?.url, brand.logo_url, input?.logo?.url, input?.logo_url],
  });
  return toRuntimeThemeState(parsed, settings.ecommerce_settings);
}

function getOrgSocialLinks(input = {}) {
  return getSocialLinks(getOrgSettings(input).ecommerce_settings);
}

function getOrgIndustryTheme(input = {}) {
  return getIndustryTheme(getOrgSettings(input).ecommerce_settings);
}

export function applyOrgRuntime(input = {}) {
  const settings = getOrgSettings(input);
  const ecommerce = settings.ecommerce_settings || {};
  const theme = getOrgTheme(input);
  const brandName = getOrgBrandName(input);
  const brandId = getBrandId(input);

  applyThemeCssVars(theme);
  useRuntimeThemeStore.getState().setThemeColors(theme);
  useRuntimeBrandStore.getState().setLogoUrl(theme.logoUrl || '');
  if (brandName) useRuntimeBrandStore.getState().setBrandName(brandName);
  const contact = getContactDetails(ecommerce);
  const policies = getPolicies(ecommerce);
  useRuntimeBrandStore.getState().setBusinessEmail(contact.email);
  useRuntimeBrandStore.getState().setSupportPhone(contact.supportPhone);
  useRuntimeBrandStore.getState().setWhatsappNumber(contact.whatsappNumber);
  useRuntimeBrandStore.getState().setPrivacyPolicy(policies.privacyPolicy);
  useRuntimeBrandStore.getState().setTermsAndConditions(policies.termsAndConditions);
  useRuntimeBrandStore.getState().setAboutUs(policies.aboutUs);
  useRuntimeBrandStore.getState().setSocialLinks(getOrgSocialLinks(input));
  setRuntimeApiKey(pickApiKey(input, { brandId }));

  return {
    brandName,
    theme,
  };
}

export {
  getAccountDetails,
  getAccountIndustryTheme,
  getAccountName,
  getAccountTheme,
  getBrandId,
  getOrgIndustryTheme,
  getOrgTheme,
  pickApiKey,
};
